import { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { Promisable } from "type-fest";

import type {
  AdministeredOrganisation,
  ConfigurationScopeId,
  ConfigurationScopeSummary,
  HolderConfiguration,
  SettingView,
} from "@thalamos/haven-common";

import { createRoute } from "../../routing/createRoute.js";
import type {
  Loaded,
  OrganisationChangeOutcome,
} from "./adminOrganisationState.js";
import { AlsoChanges } from "./AlsoChanges.js";
import { asChains, findInChains } from "./configurationChains.js";
import { ConfirmChangesDialog } from "./ConfirmChangesDialog.js";
import { bothRead, type ReadOutcome } from "./readOutcome.js";
import {
  settingsDifferingBetween,
  type ResolvedSettings,
} from "./settingDifferences.js";
import { describeSettingValue } from "./settingPresentation.js";
import {
  useSettingsOfScopes,
  type ReadScopeSettings,
} from "./useSettingsOfScopes.js";

/** The settings that apply to the organisation, as the page read them. */
type OrganisationSettingsRead = {
  holderConfiguration: HolderConfiguration | null;
  loading: boolean;
};

/** What the Configuration tab shows, as the page read it. */
export type OrganisationConfigurationData = {
  scopes: Loaded<ConfigurationScopeSummary>;
  settings: OrganisationSettingsRead;
};

/** Settings as they apply to the organisation, read only, each value in words. */
const SettingsTable = ({
  settings,
}: {
  settings: OrganisationSettingsRead;
}) => {
  const { t } = useTranslation();

  if (settings.holderConfiguration === null) {
    return settings.loading ? (
      <CircularProgress size={20} />
    ) : (
      <Alert severity="error">
        {t("admin.configuration.settings.loadFailed")}
      </Alert>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t("admin.configuration.settings.setting")}</TableCell>
          <TableCell>{t("admin.configuration.settings.applies")}</TableCell>
          <TableCell>{t("admin.configuration.settings.source")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {settings.holderConfiguration.settings.map((setting) => (
          <TableRow key={setting.key}>
            <TableCell>
              {t(`admin.configuration.settings.keys.${setting.key}`)}
            </TableCell>
            <TableCell>
              {describeSettingValue({
                t,
                key: setting.key,
                value: setting.value,
              })}
            </TableCell>
            <TableCell>
              {setting.source.kind === "default"
                ? t("admin.configuration.settings.sourceDefault")
                : setting.source.name}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export type OrganisationConfigurationTabProps = {
  organisation: AdministeredOrganisation;
  configuration: OrganisationConfigurationData;
  onSetConfigurationScope: (
    configurationScopeId: ConfigurationScopeId | null,
  ) => Promisable<OrganisationChangeOutcome>;
  /** Reads a configuration's settings, to name what else a change alters. */
  readScopeSettings: ReadScopeSettings;
};

/**
 * Which configuration an organisation is set to, where that sits in its chain,
 * and what it gives the organisation. Changing it is confirmed before anything
 * is sent.
 */
export const OrganisationConfigurationTab = ({
  organisation,
  configuration,
  onSetConfigurationScope,
  readScopeSettings,
}: OrganisationConfigurationTabProps) => {
  const { t } = useTranslation();
  const current = organisation.configurationScope;
  const [chosen, setChosen] = useState<ConfigurationScopeId | null>(
    current?.id ?? null,
  );
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chains = useMemo(
    () => asChains(configuration.scopes.rows ?? []),
    [configuration.scopes.rows],
  );
  const entries = chains.flatMap((chain) => chain.entries);
  // Without the configurations the current one is not among the options,
  // leaving only "not set" to choose.
  const scopesUnavailable = configuration.scopes.rows === undefined;
  const found =
    current === null
      ? undefined
      : findInChains({ chains, scopeId: current.id });
  const nameOf = (id: ConfigurationScopeId | null) =>
    entries.find((entry) => entry.id === id)?.name ??
    t("admin.organisation.configuration.none");

  // Read only once the change is being confirmed.
  const settingsOf = useSettingsOfScopes({
    scopeIds: confirming
      ? [current?.id ?? null, chosen].filter((id) => id !== null)
      : [],
    read: readScopeSettings,
  });
  const configurationSettings = (
    id: ConfigurationScopeId | null,
  ): ReadOutcome<ResolvedSettings> =>
    id === null ? { kind: "read", value: null } : settingsOf(id);
  const { holderConfiguration } = configuration.settings;
  const ownSettings: ReadOutcome<readonly SettingView[]> =
    holderConfiguration !== null
      ? { kind: "read", value: holderConfiguration.settings }
      : configuration.settings.loading
        ? { kind: "reading" }
        : { kind: "failed" };
  const otherChanges = bothRead({
    first: bothRead({
      first: configurationSettings(current?.id ?? null),
      second: configurationSettings(chosen),
      combine: ({ first, second }) => ({ from: first, to: second }),
    }),
    second: ownSettings,
    combine: ({ first, second }) =>
      settingsDifferingBetween({ ...first, organisationSettings: second }),
  });

  const confirm = async () => {
    setSaving(true);
    const outcome = await onSetConfigurationScope(chosen);
    setSaving(false);

    if (outcome === "failed") {
      setError(t("admin.organisation.configurationTab.changeFailed"));
      return;
    }

    setConfirming(false);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {t("admin.organisation.configurationTab.intro", {
          name: organisation.name,
        })}
      </Typography>

      {configuration.scopes.failed && (
        <Alert severity="error">
          {t("admin.organisation.configurationTab.scopesFailed")}
        </Alert>
      )}

      <Stack spacing={0.5}>
        <Typography variant="subtitle2" component="h2">
          {t("admin.organisation.configurationTab.current")}
        </Typography>
        {current === null ? (
          <Typography variant="body1">
            {t("admin.organisation.configuration.none")}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "baseline" }}>
            <Link
              component={RouterLink}
              to={createRoute.adminConfigurationScope(current.id)}
              variant="h6"
            >
              {current.name}
            </Link>
            {found !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {t("admin.organisation.configurationTab.usedBy", {
                  count: found.entry.organisationCount,
                })}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>

      {found !== undefined && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" component="h2">
            {t("admin.organisation.configurationTab.whereItSits")}
          </Typography>
          <Stack
            component="ol"
            spacing={1}
            sx={{ m: 0, p: 0, listStyle: "none" }}
          >
            {[...found.above, found.entry].map((entry) => (
              <Stack
                key={entry.id}
                component="li"
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", pl: (entry.level - 1) * 3 }}
              >
                <Chip
                  size="small"
                  label={t("admin.configuration.scope.levelOf", {
                    level: entry.level,
                  })}
                  color={entry.level === 1 ? "primary" : "default"}
                  variant={entry.level === 1 ? "filled" : "outlined"}
                />
                <Typography variant="body2">{entry.name}</Typography>
                {entry.id === found.entry.id && (
                  <Typography variant="caption" color="text.secondary">
                    {t("admin.organisation.configurationTab.usedHere")}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}

      <Stack spacing={1}>
        <Typography variant="subtitle2" component="h2">
          {t("admin.organisation.configurationTab.change")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { sm: "center" } }}
        >
          <TextField
            select
            size="small"
            label={t("admin.organisation.configurationTab.changeTo", {
              name: organisation.name,
            })}
            value={chosen ?? ""}
            disabled={scopesUnavailable}
            onChange={(event) => {
              setChosen(
                entries.find((entry) => entry.id === event.target.value)?.id ??
                  null,
              );
            }}
            sx={{ minWidth: 280 }}
          >
            <MenuItem value="">
              {t("admin.organisation.configuration.none")}
            </MenuItem>
            {entries.map((entry) => (
              <MenuItem
                key={entry.id}
                value={entry.id}
                sx={{ pl: 2 + (entry.level - 1) * 2 }}
              >
                {entry.name}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <Button
              variant="contained"
              disabled={scopesUnavailable || chosen === (current?.id ?? null)}
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
            >
              {t("admin.organisation.configurationTab.review")}
            </Button>
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t("admin.organisation.configurationTab.reviewHint")}
        </Typography>
      </Stack>

      <Accordion disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" component="span">
            {t("admin.organisation.configurationTab.settings", {
              name: organisation.name,
            })}
            {configuration.settings.holderConfiguration === null
              ? null
              : ` · ${t("admin.organisation.configurationTab.settingsCount", {
                  count:
                    configuration.settings.holderConfiguration.settings.length,
                })}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SettingsTable settings={configuration.settings} />
        </AccordionDetails>
      </Accordion>

      <ConfirmChangesDialog
        open={confirming}
        title={t("admin.organisation.configurationTab.confirmTitle", {
          name: organisation.name,
          to: nameOf(chosen),
        })}
        confirmLabel={t("admin.organisation.configurationTab.confirm")}
        saving={saving}
        error={error}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={confirm}
        confirmWaits={otherChanges.kind === "reading"}
      >
        <Typography variant="body2">
          {t("admin.organisation.configurationTab.confirmFrom", {
            from: current?.name ?? t("admin.organisation.configuration.none"),
            name: organisation.name,
          })}
        </Typography>
        <Typography variant="body2">
          {t("admin.organisation.configurationTab.confirmOnlyThis", {
            name: organisation.name,
          })}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t("admin.configuration.settings.unsaved.noAccessChange")}
        </Typography>
        <AlsoChanges changes={otherChanges} />
      </ConfirmChangesDialog>
    </Stack>
  );
};
